import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, Loader, ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { DOCUMENT_STATUSES } from '../../lib/constants';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';
import { servicesService } from '../../services/api';

export default function EnlistedConfirmModal({ onConfirm, onCancel, isSaving, prequalification }) {
  const { t } = useLanguage();
  const pq = prequalification || {};

  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => {
    servicesService.getAll().then(res => {
      setAvailableServices(Array.isArray(res) ? res : []);
    }).catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    documentStatus:       pq.documentStatus       || 'Submitted',
    submissionDate:       pq.submissionDate
      ? new Date(pq.submissionDate).toISOString().split('T')[0]
      : '',
    earnestMoneyRequired: pq.earnestMoneyRequired  ?? false,
    earnestMoneyValue:    pq.earnestMoneyValue      || '',
    proposalValue:        pq.proposalValue          || '',
    statusRemarks:        pq.statusRemarks          || '',
    services:             Array.isArray(pq.services)
      ? pq.services.map(s => typeof s === 'string' ? s : s.name).filter(Boolean)
      : [],
    remarks:              '',
  });

  const handle = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Shared style tokens â€” matches PrequalificationDetailModal / TenderDetailModal
  const labelCls  = 'block text-xs font-semibold text-gray-600 mb-1.5';
  const inputCls  = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors';
  const btnCls    = 'w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-white text-left flex justify-between items-center min-h-[42px] hover:border-gray-300 transition-colors';

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* â”€â”€ Header â”€â”€ matches TenderWonConfirmModal */}
          <div className="px-6 py-4 border-b flex justify-between items-center bg-emerald-50/50">
            <div>
              <h3 className="text-lg font-bold text-emerald-800 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                Confirm Enlistment
              </h3>
              <p className="text-sm text-emerald-600 mt-0.5">Please fill in the prequalification details before confirming.</p>
            </div>
            <button onClick={onCancel} className="p-2 hover:bg-emerald-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-emerald-700" />
            </button>
          </div>

          {/* â”€â”€ Body â”€â”€ */}
          <div className="p-6 overflow-y-auto flex-1 bg-slate-50">
            <div className="max-w-lg mx-auto space-y-5">

              {/* Section: Prequalification Details */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Prequalification Details</h4>

                <div className="grid grid-cols-2 gap-4">

                  {/* Document Status */}
                  <div>
                    <label className={labelCls}>Document Status</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button type="button" className={btnCls}>
                          <span className={formData.documentStatus ? 'text-gray-900' : 'text-gray-400'}>
                            {formData.documentStatus || 'Select status...'}
                          </span>
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover>
                        <DropdownMenu
                          selectedKeys={new Set([formData.documentStatus])}
                          selectionMode="single"
                          onSelectionChange={(keys) =>
                            setFormData(prev => ({ ...prev, documentStatus: Array.from(keys)[0] }))
                          }
                        >
                          {DOCUMENT_STATUSES.map(ds => (
                            <DropdownItem key={ds} id={ds}>{ds}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>

                  {/* Submission Date */}
                  <div>
                    <label className={labelCls}>Submission Date</label>
                    <DatePicker
                      name="submissionDate"
                      value={formData.submissionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, submissionDate: e.target.value }))}
                    />
                  </div>

                  {/* Services */}
                  {availableServices.length > 0 && (
                    <div>
                      <label className={labelCls}>Services</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button type="button" className={btnCls}>
                            <span className={formData.services.length ? 'text-gray-900 truncate mr-2' : 'text-gray-400'}>
                              {formData.services.length > 0
                                ? formData.services.join(', ')
                                : 'Select services...'}
                            </span>
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover>
                          <DropdownMenu
                            selectedKeys={new Set(formData.services)}
                            selectionMode="multiple"
                            onSelectionChange={(keys) =>
                              setFormData(prev => ({ ...prev, services: Array.from(keys) }))
                            }
                          >
                            {availableServices.map(s => (
                              <DropdownItem key={s.name} id={s.name}>{s.name}</DropdownItem>
                            ))}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>
                  )}

                  {/* Proposal Value */}
                  <div>
                    <label className={labelCls}>Proposal Value (PKR)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">PKR</span>
                      <input
                        type="number"
                        name="proposalValue"
                        value={formData.proposalValue}
                        onChange={handle}
                        placeholder="e.g. 5,000,000"
                        className={`${inputCls} pl-12`}
                      />
                    </div>
                  </div>

                  {/* Earnest Money */}
                  <div>
                    <label className={labelCls}>Earnest Money</label>
                    <div className="flex items-center gap-4 px-3 py-2.5 border border-gray-200 rounded-xl bg-white">
                      <label className="flex items-center gap-1.5 cursor-pointer text-sm font-medium text-gray-700">
                        <input
                          type="radio" name="em"
                          checked={!formData.earnestMoneyRequired}
                          onChange={() => setFormData(prev => ({ ...prev, earnestMoneyRequired: false, earnestMoneyValue: '' }))}
                        />
                        Not Required
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer text-sm font-medium text-blue-700">
                        <input
                          type="radio" name="em"
                          checked={!!formData.earnestMoneyRequired}
                          onChange={() => setFormData(prev => ({ ...prev, earnestMoneyRequired: true }))}
                        />
                        Required
                      </label>
                    </div>
                    {formData.earnestMoneyRequired && (
                      <div className="relative mt-2">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">PKR</span>
                        <input
                          type="number" name="earnestMoneyValue"
                          value={formData.earnestMoneyValue} onChange={handle}
                          placeholder="Earnest Money Amount"
                          className={`${inputCls} pl-12`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Status Remarks */}
                  <div>
                    <label className={labelCls}>Status Box / Remarks</label>
                    <input
                      type="text" name="statusRemarks"
                      value={formData.statusRemarks} onChange={handle}
                      placeholder="e.g. Submitted to PPRA"
                      className={inputCls}
                    />
                  </div>

                </div>
              </div>

              {/* Section: Note */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <label className={labelCls}>Enlistment Note <span className="font-normal text-gray-400">(Optional)</span></label>
                <textarea
                  name="remarks" value={formData.remarks} onChange={handle}
                  placeholder="Any notes about this enlistment..."
                  rows={3}
                  className={`${inputCls} resize-none`}
                />
              </div>

            </div>
          </div>

          {/* â”€â”€ Footer â”€â”€ */}
          <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(formData)}
              disabled={isSaving}
              className="px-6 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 min-w-[130px] justify-center"
            >
              {isSaving
                ? <Loader className="w-4 h-4 animate-spin" />
                : <><CheckCircle className="w-4 h-4" /> Confirm Enlistment</>
              }
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
